import TogglesLocal from './TogglesLocal';

//@ts-ignore
const isClient = typeof window !== 'undefined'
const isServer = typeof process !== 'undefined'

let configCatClient

class TogglesV2 {

    /**
     * Get the appropriate ConfigCat API key based on environment variables
     * @returns The ConfigCat API key or undefined
     */
    static getConfigCatApiKey(): string | undefined {
        let configCatApiKey = process.env.REACT_APP_CONFIGCAT_API_KEY
        const configCatMode = process.env.REACT_APP_CONFIGCAT_MODE

        if (configCatMode === '365') {
            configCatApiKey = process.env.REACT_APP_CONFIGCAT_API_KEY_DEV_365
        } else if (configCatMode === 'APP') {
            configCatApiKey = process.env.REACT_APP_CONFIGCAT_API_KEY_APP
        }

        return configCatApiKey
    }

    /**
     * Initialize TogglesManager with runtime API key (for Next.js apps)
     * @param apiKey - ConfigCat API key from runtime config
     */
    static initialize(apiKey: string) {
        if (!apiKey) {
            throw new Error('TogglesV2.initialize: apiKey is required')
        }

        if (isClient) {
            const configcatJS = require('configcat-js')
            configCatClient = configcatJS.getClient(apiKey, configcatJS.PollingMode.AutoPoll);
        } else if (isServer) {
            const configcatSSR = require('configcat-js-ssr')
            configCatClient = configcatSSR.getClient(apiKey, configcatSSR.PollingMode.AutoPoll);
        }

        configCatClient?.waitForReady().then()
    }

    static isEnabled(toggle) {
        const localValue = this.toggleLocalValue(toggle)
        const snapshot = configCatClient.snapshot()
        const configCatValue = snapshot.getValue(toggle, false)
        const result = localValue ?? configCatValue
        return Boolean(result)
    }

    static getValue(toggle) {
        const snapshot = configCatClient.snapshot()
        const configCatValue = snapshot.getValue(toggle, '')
        return configCatValue;
    }

    static isEnabledForEmail(toggle: string, email?: string): boolean {
        const localValue = this.toggleLocalValue(toggle);

        const snapshot = configCatClient.snapshot();
        const userObject = email
            ? { identifier: email, email: email }
            : undefined;

        const configCatValue = snapshot.getValue(toggle, false, userObject);

        const result = localValue ?? configCatValue;
        return Boolean(result);
    }

    static async isEnabledAsync(toggle) {
        const localValue = this.toggleLocalValue(toggle);
        await configCatClient.waitForReady()
        const snapshot = configCatClient.snapshot();
        const configCatValue = await snapshot.getValue(toggle, false)
        const result = localValue ?? configCatValue
        return Boolean(result)
    }

    static async isEnabledForEmailAsync(toggle: string, email?: string): Promise<boolean> {
        const localValue = this.toggleLocalValue(toggle);
        await configCatClient.waitForReady()
        const snapshot = configCatClient.snapshot();
        const userObject = email
            ? { identifier: email, email: email }
            : undefined;
        const configCatValue = await snapshot.getValue(toggle, false, userObject)
        const result = localValue ?? configCatValue
        return Boolean(result)
    }

    /** Like `isEnabledForEmail`, but also sends custom attributes (e.g. role) for ConfigCat targeting rules. */
    static isEnabledForUser(toggle: string, user?: { email?: string; custom?: Record<string, string> }): boolean {
        if (!user?.email && !user?.custom) {
            return this.isEnabled(toggle);
        }
        const localValue = this.toggleLocalValue(toggle);
        const snapshot = configCatClient.snapshot();
        const userObject = { identifier: user.email ?? '', ...(user.email ? { email: user.email } : {}), custom: user.custom };
        const configCatValue = snapshot.getValue(toggle, false, userObject);
        const result = localValue ?? configCatValue;
        return Boolean(result);
    }

    /** Async variant of `isEnabledForUser` (server-side: awaits ConfigCat ready). */
    static async isEnabledForUserAsync(toggle: string, user?: { email?: string; custom?: Record<string, string> }): Promise<boolean> {
        if (!user?.email && !user?.custom) {
            return this.isEnabledAsync(toggle);
        }
        const localValue = this.toggleLocalValue(toggle);
        await configCatClient.waitForReady()
        const snapshot = configCatClient.snapshot();
        const userObject = { identifier: user.email ?? '', ...(user.email ? { email: user.email } : {}), custom: user.custom };
        const configCatValue = await snapshot.getValue(toggle, false, userObject)
        const result = localValue ?? configCatValue
        return Boolean(result)
    }

    /**
     * A/B experiment variant for a user, bucketed by email (VP-1074).
     * ConfigCat percentage options hash the identifier as a literal string, so the
     * email is trimmed/lowercased to keep one user in one bucket across casings.
     * Without an email the user cannot be bucketed and gets `defaultVariant`.
     */
    static getExperimentVariant(toggle: string, email?: string, defaultVariant: string = 'control'): string {
        const localValue = this.toggleLocalValue(toggle);
        const normalizedEmail = email?.trim().toLowerCase();
        if (!normalizedEmail) {
            return String(localValue ?? defaultVariant);
        }
        const snapshot = configCatClient.snapshot();
        const userObject = { identifier: normalizedEmail, email: normalizedEmail };
        const configCatValue = snapshot.getValue(toggle, defaultVariant, userObject);
        return String(localValue ?? configCatValue ?? defaultVariant);
    }

    /** Async variant of `getExperimentVariant` (server-side: awaits ConfigCat ready). */
    static async getExperimentVariantAsync(toggle: string, email?: string, defaultVariant: string = 'control'): Promise<string> {
        const localValue = this.toggleLocalValue(toggle);
        const normalizedEmail = email?.trim().toLowerCase();
        if (!normalizedEmail) {
            return String(localValue ?? defaultVariant);
        }
        await configCatClient.waitForReady()
        const snapshot = configCatClient.snapshot();
        const userObject = { identifier: normalizedEmail, email: normalizedEmail };
        const configCatValue = await snapshot.getValue(toggle, defaultVariant, userObject)
        return String(localValue ?? configCatValue ?? defaultVariant);
    }

    static close() {
        configCatClient = null
    }

    static toggleLocalValue(toggle) {
        let value;
        if (TogglesLocal.hasOwnProperty(toggle)) {
            value = TogglesLocal[toggle];
        }
        return value;
    }

}

// Auto-initialize if REACT_APP variables are available (CRA apps)
const configCatApiKey = TogglesV2.getConfigCatApiKey()
if (configCatApiKey) {
    TogglesV2.initialize(configCatApiKey)
}

export { TogglesV2, configCatClient }

export default TogglesV2